// src/components/serverSetup/TraitSelectionModal.js

import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Divider,
  Grid,
} from '@mui/material';
import PropTypes from 'prop-types';

const TraitSelectionModal = ({
  open,
  onClose,
  roleConfigIndex,
  requirementIndex,
  traits = [], // Default to empty array
  roleConfigurations = [],
  setRoleConfigurations = () => {},
  onSaveSuccess, // New prop for callback
}) => {
  const [localSelectedTraits, setLocalSelectedTraits] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Get the collectionAddress for the current requirement
  const collectionAddress = useMemo(() => {
    if (
      roleConfigIndex !== null &&
      requirementIndex !== null &&
      roleConfigurations[roleConfigIndex] &&
      roleConfigurations[roleConfigIndex].requirements[requirementIndex]
    ) {
      return roleConfigurations[roleConfigIndex].requirements[requirementIndex].collectionAddress;
    }
    return '';
  }, [roleConfigIndex, requirementIndex, roleConfigurations]);

  // Debugging: Log collectionAddress and roleConfigurations
  useEffect(() => {
    console.log('TraitSelectionModal - collectionAddress:', collectionAddress);
    console.log('TraitSelectionModal - roleConfigurations:', roleConfigurations);
  }, [collectionAddress, roleConfigurations]);

  // Populate localSelectedTraits when the modal opens or roleConfigIndex/requirementIndex changes
  useEffect(() => {
    if (
      collectionAddress &&
      roleConfigIndex !== null &&
      requirementIndex !== null &&
      roleConfigurations[roleConfigIndex] &&
      roleConfigurations[roleConfigIndex].requirements[requirementIndex]
    ) {
      const existingTraits = roleConfigurations[roleConfigIndex].requirements[requirementIndex].traits;
      setLocalSelectedTraits(existingTraits);
    } else {
      setLocalSelectedTraits([]);
    }
  }, [collectionAddress, roleConfigIndex, requirementIndex, roleConfigurations, open]);

  const handleLocalTraitChange = (traitKey) => {
    setLocalSelectedTraits((prev) => {
      if (prev.includes(traitKey)) {
        return prev.filter((t) => t !== traitKey);
      } else {
        return [...prev, traitKey];
      }
    });
  };

  const handleSaveTraits = () => {
    setRoleConfigurations((prev) => {
      const updated = [...prev];
      if (!updated[roleConfigIndex]) {
        updated[roleConfigIndex] = { ...updated[roleConfigIndex], requirements: [] };
      }
      if (!updated[roleConfigIndex].requirements[requirementIndex]) {
        updated[roleConfigIndex].requirements[requirementIndex] = { ...updated[roleConfigIndex].requirements[requirementIndex], traits: [] };
      }
      updated[roleConfigIndex].requirements[requirementIndex].traits = localSelectedTraits;
      return updated;
    });
    // Invoke the callback to notify parent component
    if (onSaveSuccess) {
      onSaveSuccess('Traits saved successfully!');
    }
    onClose();
  };

  // Flatten the nested traits array
  const flatTraits = useMemo(() => {
    return traits.flatMap((trait) =>
      trait.values.map((valueObj) => ({
        trait_type: trait.trait_type,
        value: valueObj.value,
        count: valueObj.count,
        collectionAddress: trait.collectionAddress,
      }))
    );
  }, [traits]);

  // Filter traits based on searchTerm and collectionAddress with defensive checks
  const filteredTraits = useMemo(() => {
    return flatTraits
      .filter((trait) => trait.collectionAddress === collectionAddress)
      .filter((trait) => {
        const hasValue = trait.value && typeof trait.value === 'string';
        const hasTraitType = trait.trait_type && typeof trait.trait_type === 'string';
        const valueMatch = hasValue ? trait.value.toLowerCase().includes(searchTerm.toLowerCase()) : false;
        const typeMatch = hasTraitType ? trait.trait_type.toLowerCase().includes(searchTerm.toLowerCase()) : false;
        return valueMatch || typeMatch;
      });
  }, [flatTraits, collectionAddress, searchTerm]);

  // Group filteredTraits by trait_type
  const groupedTraits = useMemo(() => {
    return filteredTraits.reduce((acc, trait) => {
      const { trait_type } = trait;

      // Exclude 'rarity' trait_type if it exists
      if (trait_type.toLowerCase() === 'rarity') {
        return acc;
      }

      if (!acc[trait_type]) {
        acc[trait_type] = [];
      }
      acc[trait_type].push(trait);
      return acc;
    }, {});
  }, [filteredTraits]);

  // Debugging: Log groupedTraits
  useEffect(() => {
    console.log('TraitSelectionModal - groupedTraits:', groupedTraits);
  }, [groupedTraits]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          color: '#E0E4ED',
          backgroundColor: '#040813',
          fontFamily: 'Poppins, sans-serif',
        }}
      >
        Select Traits for Collection: {collectionAddress}
      </DialogTitle>
      <DialogContent
        sx={{
          backgroundColor: '#0D1F3A',
          padding: 3,
          maxHeight: '70vh', // Limit modal height
          overflowY: 'auto', // Enable scrolling within the content
        }}
      >
        <TextField
          label="Search Traits"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
          InputProps={{
            style: {
              color: '#E0E4ED',
              backgroundColor: '#132A47',
              borderRadius: '8px',
            },
          }}
          InputLabelProps={{
            style: {
              color: '#D1E5F0',
            },
          }}
          sx={{
            marginTop: 2,
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: '#607080',
              },
              '&:hover fieldset': {
                borderColor: '#9AC0CD',
              },
            },
            mb: 2,
          }}
        />
        {Object.keys(groupedTraits).length > 0 ? (
          Object.entries(groupedTraits).map(([traitType, traitsList]) => (
            <Box key={traitType} sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{ color: '#9AC0CD', fontFamily: 'Poppins, sans-serif', mb: 1 }}
              >
                {traitType}
              </Typography>
              <Grid container spacing={2}>
                {traitsList.map((trait) => {
                  const traitKey = `${trait.trait_type}: ${trait.value}`;
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={traitKey}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={localSelectedTraits.includes(traitKey)}
                            onChange={() => handleLocalTraitChange(traitKey)}
                            sx={{
                              color: '#4C5C64',
                              '&.Mui-checked': {
                                color: '#9AC0CD',
                              },
                            }}
                          />
                        }
                        label={`${trait.value} (${trait.count})`}
                        sx={{ color: '#E0E4ED', fontFamily: 'Poppins, sans-serif' }}
                      />
                    </Grid>
                  );
                })}
              </Grid>
              <Divider sx={{ mt: 2, backgroundColor: '#607080' }} />
            </Box>
          ))
        ) : (
          <Typography sx={{ color: '#E0E4ED' }}>No traits found for this collection.</Typography>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          backgroundColor: '#040813',
          padding: 2,
        }}
      >
        <Button
          onClick={handleSaveTraits}
          sx={{
            color: '#A1D1F0',
            borderColor: '#4C5C64',
            textTransform: 'none',
            '&:hover': {
              borderColor: '#9AC0CD',
              backgroundColor: 'rgba(154, 192, 205, 0.1)',
            },
          }}
          variant="outlined"
        >
          Save
        </Button>
        <Button
          onClick={onClose}
          sx={{
            color: '#A1D1F0',
            borderColor: '#4C5C64',
            textTransform: 'none',
            '&:hover': {
              borderColor: '#9AC0CD',
              backgroundColor: 'rgba(154, 192, 205, 0.1)',
            },
          }}
          variant="outlined"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

TraitSelectionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  roleConfigIndex: PropTypes.number,
  requirementIndex: PropTypes.number,
  traits: PropTypes.arrayOf(
    PropTypes.shape({
      collectionAddress: PropTypes.string.isRequired,
      trait_type: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
    })
  ).isRequired,
  roleConfigurations: PropTypes.array.isRequired,
  setRoleConfigurations: PropTypes.func.isRequired,
  onSaveSuccess: PropTypes.func, // Define the new prop
};

export default TraitSelectionModal;

